/* eslint-disable max-lines */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Grid,
  ButtonBase,
  Typography,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Tooltip,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Chip,
  Badge,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  Refresh,
  Edit as EditIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  Password as PasswordIcon,
  WifiPassword as WifiPasswordIcon,
  Call as CallIcon,
  Mail as MailIcon,
  MobileFriendly as MobileFriendlyIcon,
  Person as PersonIcon,
  CurrencyExchangeRounded as CurrencyExchangeRoundedIcon,
  PendingActions as PendingActionsIcon,
  Payments as PaymentsIcon,
  CreditScore as CreditScoreIcon,
  MoreVert as MoreVertIcon,
  AccountBalanceWallet as WalletIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  SystemUpdateAlt as SystemUpdateAltIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  ExpandMore as ExpandMoreIcon,
  ManageAccounts as ManageAccountsIcon,
  SettingsInputComponent as ConnectivityIcon,
  CameraAlt as CameraAltIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  VpnKey as VpnKeyIcon,
  AppSettingsAlt as AppSettingsAltIcon,
  Menu as MenuIcon,
  Bolt as BoltIcon,
  Search as SearchIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Analytics as AnalyticsIcon,
  Description as DescriptionIcon,
  Assessment as AssessmentIcon,
  Public as PublicIcon,
  Star as StarIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { orange, blue, green, grey, red } from "@mui/material/colors";
import emptyBoxDocument from "../../../assets/illustrations/empty_box_document.png";
import { a11yProps } from "../../../utils/common";
import TabPanel from "../../../components/TabPanel";
import useUser from "../../../hooks/useUser";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import GeneralInformation, {
  IdentityWidget,
  BusinessWidget,
  StatusWidget,
} from "./GeneralInformation";
import ContactInformation, { LocationWidget, AccountHistoryWidget } from "./ContactInformation";
import AccountInformation, {
  AccountConfigurationWidget,
  NetworkSecurityWidget,
} from "./AccountInformation";
import PlanInformation from "./PlanInformation";
import ProfileImg from "../../icons/profile_img.png";
import RenewUser from "../userlist/components/RenewUser";
import ChangePlan from "../userlist/components/ChangePlan";
import ChangeAppPassword from "../userlist/components/ChangeAppPass";
import ChangeRouterPassword from "../userlist/components/ChangeRouterPass";
import PaidInvoice from "../../invoices/paid_invoices";
import UnpaidInvoice from "../../invoices/unpaid_invoices";
import WalletEntries from "./WalletEntries";
import PlanHistory from "./PlanHistory";
import Complaint from "../../complaint";
import TrafficReport from "./TrafficReport";
import ProformaInvoices from "./ProformaInvoices";
import SessionHistory from "./SessionHistory";
import CancelledInvoices from "../../users/user_details/CancelledInvoices";
import AuthenticationLogs from "./AuthenticationLogs";
import KycDocuments from "./KycDocuments";
import Materials from "./materials";
import Tr069Device from "./Tr069Device";
import ScheduleRenewalScreen from "../userlist/components/Schedule";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import AutoRenewal from "../userlist/components/AutoRenewal";
import useScheduleUser from "../../../hooks/useScheduleUser";
import RunningWithErrorsIcon from "@mui/icons-material/RunningWithErrors";
import SuspendUser from "../userlist/components/SuspendUser";
import AddWallet from "../userlist/components/AddWallet";
import DisconnectUser from "./DisconnectUser";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ProformaInvoice from "../userlist/components/ProformaInvoice";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import UpdateDisabledIcon from "@mui/icons-material/UpdateDisabled";
import CloseSession from "./CloseSession";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import SpeedGraph from "./SpeedGraph";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShowAppPassword from "../userlist/components/ShowApp";
import ShowRouterPass from "../userlist/components/ShowRouter";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import ChangeValidity from "../userlist/components/ChangeValidity";
import OtherDetails from "./OtherDetails";
import ChangeUserName from "../userlist/components/ChangeUserName";
import BadgeIcon from "@mui/icons-material/Badge";
import HoldUser from "../userlist/components/HoldUser";
import PanToolIcon from "@mui/icons-material/PanTool";
import TimerIcon from "@mui/icons-material/Timer";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import DvrIcon from "@mui/icons-material/Dvr";
import StaticIp from "../userlist/components/StaticIpAddress";
import ArticleIcon from "@mui/icons-material/Article";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReceiptForm from "./ReceiptPage";
import SessionClose from "./SessionClose";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignPlan from "../userlist/components/AssignPlan";
import useAuth from "../../../hooks/useAuth";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import { useFormContext } from "react-hook-form";
import UpdateDataLimit from "../userlist/components/UpdateDataLimit";
import Receipts from "./Receipts";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import UserSummary, { ProfileWidget, PlanWidget, FinancialWidget } from "./UserSummary";
import ActionMenu from "./ActionMenu";
import { Joyride, STATUS } from "react-joyride";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [
    {
      field: "createdAt",
      sort: "DESC",
    },
  ],
};
const Img = styled("img")({
  margin: "auto",
  display: "block",
  maxWidth: "100%",
  maxHeight: "100%",
});

const StyledStatCard = styled(Paper)(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "24px",
  background: bgcolor
    ? `linear-gradient(135deg, ${alpha(bgcolor, 0.12)} 0%, ${theme.palette.background.paper} 100%)`
    : theme.palette.background.paper,
  boxShadow: "0 10px 30px -5px rgba(0,0,0,0.05)",
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  height: "100%",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 20px 40px -10px rgba(0,0,0,0.12)",
    borderColor: bgcolor ? alpha(bgcolor, 0.4) : theme.palette.divider,
  },
}));

const HeaderCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(4),
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

const ActionTile = styled(ButtonBase)(({ theme, color = "primary" }) => {
  const mainColor = theme.palette[color]?.main || theme.palette.primary.main;

  return {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(1.2, 2),
    borderRadius: "14px",
    background: theme.palette.background.paper,
    border: `1px solid ${alpha(mainColor, 0.25)}`,
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    width: "100%",
    minHeight: "52px",
    gap: theme.spacing(1.5),
    textAlign: "left",
    boxShadow: `0 2px 8px ${alpha(mainColor, 0.08)}`,
    "& .tile-icon-wrapper": {
      width: "32px",
      height: "32px",
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: alpha(mainColor, 0.12),
      color: mainColor,
      transition: "all 0.25s ease",
      boxShadow: `inset 0 0 0 1px ${alpha(mainColor, 0.1)}`,
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.2rem",
    },
    "& .MuiTypography-root": {
      fontWeight: 900,
      fontSize: "0.75rem",
      color: theme.palette.text.primary,
      textTransform: "uppercase",
      letterSpacing: "0.02em",
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "&:hover": {
      background: alpha(mainColor, 0.04),
      borderColor: mainColor,
      transform: "translateY(-3px)",
      boxShadow: `0 8px 24px ${alpha(mainColor, 0.15)}`,
      "& .tile-icon-wrapper": {
        background: mainColor,
        color: "white",
        transform: "scale(1.1) rotate(-5deg)",
      },
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  };
});

const StyledTabs = styled(Tabs)(({ theme }) => ({
  padding: theme.spacing(1),
  background: alpha(theme.palette.primary.main, 0.04),
  borderRadius: "16px",
  margin: theme.spacing(2, 2, 2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  minHeight: "auto",
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 800,
  fontSize: "0.85rem",
  padding: theme.spacing(1.5, 3),
  minHeight: "auto",
  borderRadius: "14px",
  color: theme.palette.text.secondary,
  transition: "all 0.2s ease",
  marginRight: theme.spacing(1),
  "&.Mui-selected": {
    color: "white",
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  "&:hover:not(.Mui-selected)": {
    background: alpha(theme.palette.primary.main, 0.05),
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  margin: theme.spacing(4, 0, 3),
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: "2px",
    background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0)}, ${
      theme.palette.primary.main
    }, ${alpha(theme.palette.primary.main, 0)})`,
    opacity: 0.5,
  },
  "& .MuiTypography-root": {
    padding: theme.spacing(0, 3),
    color: theme.palette.text.secondary,
    fontWeight: 800,
    fontSize: "0.75rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "28px",
  height: "100%",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  boxShadow: "0 4px 24px rgba(0,0,0,0.03)",
}));

const fadeInUp = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const pulse = {
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(59, 130, 246, 0.4)" },
    "70%": { boxShadow: "0 0 0 10px rgba(59, 130, 246, 0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(59, 130, 246, 0)" },
  },
};

const HeaderActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "colorScheme",
})(({ theme, colorScheme = "primary" }) => {
  const mainColor = theme.palette[colorScheme]?.main || theme.palette.primary.main;
  const lightColor = theme.palette[colorScheme]?.light || theme.palette.primary.light;

  return {
    borderRadius: "50px",
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.725rem",
    padding: "6px 16px 6px 6px",
    background: `linear-gradient(135deg, ${alpha(mainColor, 0.1)} 0%, ${alpha(mainColor, 0.18)} 100%)`,
    border: `1px solid ${alpha(mainColor, 0.4)}`,
    borderLeft: `4px solid ${mainColor}`,
    color: theme.palette.text.primary,
    boxShadow: `0 4px 14px ${alpha(mainColor, 0.15)}`,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& .MuiButton-startIcon": {
      color: "white",
      background: mainColor,
      padding: "6px",
      borderRadius: "50%",
      margin: 0,
      boxShadow: `0 2px 8px ${alpha(mainColor, 0.3)}`,
      transition: "all 0.3s ease",
      "& svg": {
        fontSize: "1rem",
      },
    },
    "& .MuiButton-endIcon": {
      color: alpha(theme.palette.text.secondary, 0.4),
      transition: "all 0.3s ease",
    },
    "&:hover": {
      background: `linear-gradient(135deg, ${mainColor} 0%, ${lightColor} 100%)`,
      color: "white",
      borderColor: mainColor,
      transform: "translateY(-3px)",
      boxShadow: `0 8px 24px ${alpha(mainColor, 0.35)}`,
      "& .MuiButton-startIcon": {
        color: mainColor,
        background: "white",
        transform: "scale(1.1) rotate(-10deg)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
      },
      "& .MuiButton-endIcon": {
        color: "white",
        transform: "rotate(180deg)",
      },
    },
  };
});

function NormalUser(props) {
  const { width: mainContentWidth } = useMainContentDimensions();
  console.log(mainContentWidth, "mainContentWidth");
  let [searchParams, setSearchParams] = useSearchParams();
  const {
    getUser,
    user,
    getUserDetailsInvoice,
    userDetailsInvoice,
    isGetUserLoading,
    updateOnHold,
    suspendUser,
    uploadProfilePicture,
  } = useUser();
  const { getActiveScheduleUser } = useScheduleUser();
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const dispatch = useDispatch();
  const [payload, setPayload] = useState(currentPayload);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isMsoVisible, setIsMsoVisible] = useState(true);
  const { user: operator } = useAuth();

  const [{ runTour, steps }, setTourState] = useState({
    runTour: false,
    steps: [
      {
        target: ".tour-user-summary",
        content: "Here you can see the user's primary details, plan, and data consumption.",
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: ".tour-quick-actions",
        content: "Click here to view quick management actions for this user.",
        placement: "left",
      },
      {
        target: ".tour-refresh",
        content: "Use this to refresh the user's data.",
        placement: "bottom",
      },
      {
        target: ".tour-info-details",
        content: "This section contains General, Contact, and Account Information.",
        placement: "top",
      },
      {
        target: ".tour-activity-tabs",
        content:
          "Navigate through these tabs to view invoices, wallet entries, and other historical data.",
        placement: "top",
      },
    ],
  });

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setTourState((prev) => ({ ...prev, runTour: false }));
    }
  };

  const fileInputRef = React.useRef(null);
  const [profilePreview, setProfilePreview] = React.useState(null);

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];

    if (file && user) {
      console.log("Selected file:", file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
      };
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await uploadProfilePicture(user.radUserId, formData);

        console.log("Upload response:", response);

        dispatch(showSuccessSnackbar("Profile picture updated successfully"));

        await fetchData();
        console.log("User After Fetch", user);
      } catch (err) {
        console.error("Upload failed", err);
        dispatch(showErrorSnackbar("Failed to update profile picture"));
      }
    }
  };

  // Construct profile image URL
  const profilePicture = React.useMemo(() => {
    const apiURL = process.env.REACT_APP_API_URL || "";
    const BASE_URL =
      process.env.REACT_APP_API_BASE_URL || apiURL.replace(/\/api\/v1\/?$/, "").replace(/\/$/, "");

    if (profilePreview) {
      return profilePreview;
    }

    const getFileName = (path) => {
      if (!path) return null;
      return path.split("/").pop();
    };

    if (user?.profilePicture?.trim()) {
      return `${BASE_URL}/${user.profilePicture}?v=${Date.now()}`;
    }

    if (user?.cafProfileProof?.trim()) {
      // Pass the full stored path as a query param so the server can resolve the file on disk
      return `${BASE_URL}/api/v1/caf/preview/${user.cafId}?filePath=${encodeURIComponent(user.cafProfileProof)}&v=${Date.now()}`;
    }

    return ProfileImg;
  }, [user, profilePreview]);
  console.log("cafProfileProof", user?.cafProfileProof);
  console.log("Profile URL:", profilePicture);
  console.log("Profile path from API:", user?.profilePicture);
  useEffect(() => {
    console.log("profilePicture field:", user?.profilePicture);
    console.log("Generated image URL:", profilePicture);
  }, [user, profilePicture]);

  const fetchData = async () => {
    await getUser(searchParams.get("id"));
  };

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const refreshPage = () => {
    setPayload(currentPayload);
    fetchData();
    filterResetFunc();
  };
  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const [anchorElAccount, setAnchorElAccount] = useState(null);
  const [anchorElBilling, setAnchorElBilling] = useState(null);
  const [anchorElConnectivity, setAnchorElConnectivity] = useState(null);

  const handleOpenAccount = (event) => setAnchorElAccount(event.currentTarget);
  const handleCloseAccount = () => setAnchorElAccount(null);
  const handleOpenBilling = (event) => setAnchorElBilling(event.currentTarget);
  const handleCloseBilling = () => setAnchorElBilling(null);
  const handleOpenConnectivity = (event) => setAnchorElConnectivity(event.currentTarget);
  const handleCloseConnectivity = () => setAnchorElConnectivity(null);
  const userId = searchParams.get("id");
  const fetchScheduleData = () => {
    if (userId) {
      getActiveScheduleUser(userId);
    }
  };
  useEffect(() => {
    if (userId) {
      getActiveScheduleUser(userId);
    }
  }, [userId]);
  const editUser = () => {
    navigate("/user/userlist/edit", { state: { id: searchParams.get("id") } });
  };

  if (user) {
    console.log(user.businessType, "busineesss");
  }

  const renewClick = (id) => {
    return openDialog(
      "renew_user",
      "Renew User",
      <RenewUser
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        fetchScheduleData={fetchScheduleData}
        refreshPage={refreshPage}
      />,
      "md",
    );
  };
  const changePlan = (id) => {
    return openDialog(
      "change_plan",
      "Change plan",
      <ChangePlan key={getTimeStamp()} user={user} refreshPage={refreshPage} />,
    );
  };
  const changeRouterPass = (id) => {
    return openDialog(
      "change_router_password",
      "Change Router Password",
      <ChangeRouterPassword
        key={getTimeStamp()}
        userName={user?.userName}
        radUserId={user?.radUserId}
        refreshPage={refreshPage}
      />,
    );
  };

  const changeUserName = (id) => {
    return openDialog(
      "change_user_name",
      "Change UserName",
      <ChangeUserName
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const staticIp = (id) => {
    return openDialog(
      "static_ip",
      "Static Ip Address",
      <StaticIp key={getTimeStamp()} userId={searchParams.get("id")} />,
    );
  };
  const assignPlan = (id) => {
    return openDialog(
      "assign_plan",
      "Assign Plan",
      <AssignPlan key={getTimeStamp()} userId={searchParams.get("id")} />,
      "md",
    );
  };

  const receipt = (id) => {
    return openDialog(
      "receipt",
      "Receipt",
      <ReceiptForm key={getTimeStamp()} userId={searchParams.get("id")} />,
      "md",
    );
  };

  const changeAppPass = (id) => {
    return openDialog(
      "change_app_password",
      "Change App Password",
      <ChangeAppPassword
        key={getTimeStamp()}
        userName={user.userName}
        radUserId={user?.radUserId}
        refreshPage={refreshPage}
      />,
    );
  };
  // const changeScheduleRenew = (id) => {
  //   return openDialog(
  //     "schedule",
  //     "Schedule",
  //     <ScheduleRenewalScreen key={getTimeStamp()} userId={searchParams.get("id")} />,
  //   );
  // };
  const caRenewal = (id) => {
    return openDialog(
      "auto_renewal",
      "Auto Renewal",
      <AutoRenewal key={getTimeStamp()} user={user} />,
    );
  };
  const suspenduser = (id) => {
    return openDialog(
      "suspend",
      "Suspend",
      <SuspendUser key={getTimeStamp()} user={user} fetchData={fetchData} />,
    );
  };

  const addWallet = (id) => {
    return openDialog(
      "add_wallet",
      "Add Wallet",
      <AddWallet key={getTimeStamp()} userId={searchParams.get("id")} refreshPage={refreshPage} />,
    );
  };
  const invoiceClick = (id) => {
    return openDialog(
      "proforma_invoice",
      "Proforma Invoice",
      <ProformaInvoice key={getTimeStamp()} user={user} />,
    );
  };
  const disconnectUser = () => {
    return openDialog(
      "disconnect_user",
      "Logout User",
      <DisconnectUser
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const sessionClose = () => {
    return openDialog(
      "session_close",
      "Session Close",
      <SessionClose
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const closeClick = (id) => {
    return openDialog(
      "close_user_session",
      "Close User Session",
      <CloseSession
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const showClick = (id) => {
    return openDialog(
      "show_graph",
      "Show Graph",
      <SpeedGraph
        key={getTimeStamp()}
        userId={user?.userName}
        nasIpAddress={user?.nasIpAddress}
        refreshPage={refreshPage}
      />,
    );
  };

  const showRouterClick = (id) => {
    return openDialog(
      "show_router_password",
      "Show Router Password",
      <ShowRouterPass
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const showAppClick = (id) => {
    return openDialog(
      "show_app_password",
      "Show App Password",
      <ShowAppPassword
        key={getTimeStamp()}
        userId={searchParams.get("id")}
        refreshPage={refreshPage}
      />,
    );
  };

  const changeValidityClick = (id) => {
    return openDialog(
      "change_validity",
      "Change Validity",
      <ChangeValidity key={getTimeStamp()} user={user} refreshPage={refreshPage} />,
    );
  };

  const holdClick = (id) => {
    return openDialog(
      "hold_user",
      "Hold User",
      <HoldUser key={getTimeStamp()} user={user} refreshPage={refreshPage} />,
    );
  };

  const updateDataLimit = (id) => {
    return openDialog(
      "update_data",
      "Update Data",
      <UpdateDataLimit
        key={getTimeStamp()}
        user={user}
        refreshPage={refreshPage}
        userId={searchParams.get("id")}
      />,
    );
  };

  const theme = useTheme();
  const handlers = {
    changeUserName,
    changeRouterPass,
    changeAppPass,
    suspenduser,
    holdClick,
    updateDataLimit,
    changePlan,
    assignPlan,
    renewClick,
    addWallet,
    changeValidityClick,
    invoiceClick,
    disconnectUser,
    showClick,
    showRouterClick,
    showAppClick,
    caRenewal,
    staticIp,
    editUser,
    refreshPage,
  };

  return (
    <React.Fragment>
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={runTour}
        scrollToFirstStep
        showProgress
        showSkipButton
        steps={steps}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: theme.palette.primary.main,
          },
        }}
      />
      {isGetUserLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 2, md: 4 },
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.default,
              0.9,
            )} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            minHeight: "100vh",
            position: "relative",
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*"
          />
          {/* TOP HEADER */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 4,
              gap: 2,
              animation: "fadeInUp 0.6s ease-out forwards",
              ...fadeInUp,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box sx={{ pl: 0.8, mr: 1 }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 900, color: "text.primary", letterSpacing: "-0.02em" }}
                >
                  User Details
                </Typography>
              </Box>

              <HeaderActionButton
                onClick={handleOpenAccount}
                startIcon={<ManageAccountsIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                className="tour-quick-actions"
                colorScheme="primary"
              >
                Account & Access
              </HeaderActionButton>

              <HeaderActionButton
                onClick={handleOpenBilling}
                startIcon={<WalletIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                colorScheme="success"
              >
                Billing & Plans
              </HeaderActionButton>

              <HeaderActionButton
                onClick={handleOpenConnectivity}
                startIcon={<ShowChartIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                colorScheme="info"
              >
                Tools & Diagnostics
              </HeaderActionButton>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setTourState((prev) => ({ ...prev, runTour: true }))}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "white",
                  color: "text.primary",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                Page Tour
              </Button>
              <Chip
                label={(user.onHoldFromDate ? "HOLD" : user.userStatus || "INACTIVE").toUpperCase()}
                sx={{
                  bgcolor: (() => {
                    const status = user.onHoldFromDate ? "hold" : user.userStatus?.toLowerCase();
                    if (status === "active") return "#3b82f6";
                    if (status === "expired") return "#f97316";
                    if (status === "suspended") return "#ef4444";
                    if (status === "on_hold" || status === "hold") return "#eab308";
                    return alpha(theme.palette.divider, 0.1);
                  })(),
                  color: (() => {
                    const status = user.onHoldFromDate ? "hold" : user.userStatus?.toLowerCase();
                    if (
                      status === "active" ||
                      status === "expired" ||
                      status === "suspended" ||
                      status === "on_hold" ||
                      status === "hold"
                    )
                      return "white";
                    return "text.secondary";
                  })(),
                  fontWeight: 900,
                  fontSize: "0.7rem",
                  height: "32px",
                  borderRadius: "10px",
                  px: 1,
                  animation:
                    user.userStatus === "active" && !user.onHoldFromDate
                      ? "pulse 2s infinite"
                      : "none",
                  ...pulse,
                }}
              />

              <IconButton
                onClick={refreshPage}
                className="tour-refresh"
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "white",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: "12px",
                  p: 1.2,
                }}
              >
                <Refresh sx={{ fontSize: 20, color: "text.primary" }} />
              </IconButton>
            </Stack>
          </Box>

          <Grid
            container
            spacing={3}
            sx={{ animation: "fadeInUp 0.6s ease-out 0.2s forwards", opacity: 0, ...fadeInUp }}
          >
            {/* 1. Identity (Profile) */}
            <Grid item xs={12} md={4}>
              <ProfileWidget
                user={user}
                profilePicture={profilePicture}
                handleAvatarClick={handleAvatarClick}
              />
            </Grid>

            {/* 2. Status */}
            <Grid item xs={12} md={4}>
              <StatusWidget user={user} />
            </Grid>

            {/* 3. Current Plan */}
            <Grid item xs={12} md={4}>
              <PlanWidget
                user={user}
                renewUser={renewClick}
                permissionKeys={props.permissionKeys}
                operator={operator}
              />
            </Grid>
            {/* 4. Account Configuration */}
            <Grid item xs={12} md={6}>
              <AccountConfigurationWidget user={user} />
            </Grid>
            {/* 5. Network and Security */}
            <Grid item xs={12} md={6}>
              <NetworkSecurityWidget user={user} />
            </Grid>

            {/* 6. Business */}
            <Grid item xs={12} md={6}>
              <BusinessWidget user={user} />
            </Grid>

            {/* 7. Location */}
            <Grid item xs={12} md={6}>
              <LocationWidget user={user} />
            </Grid>

            {/* 8. Identity */}
            <Grid item xs={12} md={4}>
              <IdentityWidget user={user} />
            </Grid>

            {/* 9. Account History */}
            <Grid item xs={12} md={4}>
              <AccountHistoryWidget user={user} />
            </Grid>

            {/* 10. Financial Status */}
            <Grid item xs={12} md={4}>
              <FinancialWidget user={user} userDetailsInvoice={userDetailsInvoice} />
            </Grid>

            <Grid item xs={12}>
              <SectionHeader sx={{ mt: 5 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: "1.2rem", color: "primary.main" }} />
                <Typography>Activity & Records</Typography>
              </SectionHeader>

              {/* CONTENT TABS SECTION */}
              <Paper
                className="tour-activity-tabs"
                elevation={0}
                sx={{
                  borderRadius: "28px",
                  overflow: "hidden",
                  border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
                  bgcolor: "background.paper",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <StyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ minHeight: "48px" }}
                  >
                    <StyledTab label="Unpaid Invoices" {...a11yProps(0)} />
                    <StyledTab label="Paid Invoices" {...a11yProps(1)} />
                    {/* <StyledTab label="Wallet Entries" {...a11yProps(2)} /> */}
                    <StyledTab label="Cancelled Invoices" {...a11yProps(2)} />
                    <StyledTab label="Receipts" {...a11yProps(3)} />
                    <StyledTab label="Plan History" {...a11yProps(4)} />
                    <StyledTab label="KYC Documents" {...a11yProps(5)} />
                    <StyledTab label="Complaints" {...a11yProps(6)} />
                    <StyledTab label="Traffic Report" {...a11yProps(7)} />
                    <StyledTab label="Proforma Invoices" {...a11yProps(8)} />
                    <StyledTab label="Session History" {...a11yProps(9)} />
                    <StyledTab label="Authentication Logs" {...a11yProps(10)} />
                    <StyledTab label="Materials" {...a11yProps(11)} />
                    <StyledTab label="Router (TR-069)" {...a11yProps(12)} />
                  </StyledTabs>
                </Box>
                <Box sx={{ p: 3, minHeight: "350px", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <TabPanel value={tabValue} index={0}>
                      <UnpaidInvoice radUserId={user?.radUserId} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                      <PaidInvoice radUserId={user?.radUserId} />
                    </TabPanel>
                    {/* <TabPanel value={tabValue} index={2}>
                      <WalletEntries />
                    </TabPanel> */}
                    <TabPanel value={tabValue} index={2}>
                      <CancelledInvoices
                        setPayload={setPayload}
                        payload={payload}
                        setFilterResetFunc={setFilterResetFunc}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={3}>
                      <Receipts radUserId={user?.radUserId} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <PlanHistory />
                        </Grid>
                      </Grid>
                    </TabPanel>
                    <TabPanel value={tabValue} index={5}>
                      <KycDocuments
                        radUserId={user?.radUserId}
                        cafId={user.cafId}
                        cafStatus={user.cafStatus}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={6}>
                      <Complaint
                        radUserId={user?.radUserId}
                        userName={user?.userName}
                        backTo={"/user/details?id=" + user?.radUserId}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={7}>
                      <TrafficReport userName={user?.userName} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={8}>
                      <ProformaInvoices />
                    </TabPanel>
                    <TabPanel value={tabValue} index={9}>
                      <SessionHistory userName={user?.userName} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={10}>
                      <AuthenticationLogs userName={user?.userName} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={11} keepMounted>
                      <Materials userId={user?.radUserId} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={12}>
                      <Tr069Device radUserId={user?.radUserId} />
                    </TabPanel>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* CONSOLIDATED ACTIONS COMMAND CENTER */}
          <ActionMenu
            type="account"
            anchorEl={anchorElAccount}
            onClose={handleCloseAccount}
            permissionKeys={props.permissionKeys}
            user={user}
            operator={operator}
            theme={theme}
            handlers={handlers}
          />

          <ActionMenu
            type="billing"
            anchorEl={anchorElBilling}
            onClose={handleCloseBilling}
            permissionKeys={props.permissionKeys}
            user={user}
            operator={operator}
            theme={theme}
            handlers={handlers}
          />

          <ActionMenu
            type="tools"
            anchorEl={anchorElConnectivity}
            onClose={handleCloseConnectivity}
            permissionKeys={props.permissionKeys}
            user={user}
            operator={operator}
            theme={theme}
            handlers={handlers}
          />
        </Box>
      )}
    </React.Fragment>
  );
}

export default NormalUser;
